import { useGetConversationQuery } from "data/conversation";
import { useSearchBookingsQuery, useSearchPropertiesQuery } from "data/search";
import { useMemo } from "react";

export const useMultiplatData = (guestEmail, conversationId) => {
  const {
    data: bookings,
    isLoading: isBookingsLoading,
    error: bookingsError,
    refetch: refetchBookings
  } = useSearchBookingsQuery({ guestEmail }, { skip: !guestEmail });
  const {
    data: conversation,
    isLoading: isConversationLoading,
    error: conversationError,
    refetch: refetchConversation
  } = useGetConversationQuery({ conversationId }, { skip: !conversationId });
  const {
    data: property,
    isLoading: isPropertyLoading,
    error: propertyError
  } = useSearchPropertiesQuery(
    { homeCode: conversation?.listing?.homecode },
    { skip: !conversation?.listing?.homecode }
  );

  const select = useMemo(() => {
    const reservation = {
      number_of_guests: conversation?.reservation?.number_of_guests,
      number_of_adults: conversation?.reservation?.number_of_adults,
      number_of_children: conversation?.reservation?.number_of_children,
      number_of_infants: conversation?.reservation?.number_of_infants,
      number_of_pets: conversation?.reservation?.number_of_pets,
      status: conversation?.reservation?.status,
      confirmation_code: conversation?.reservation?.confirmation_code,
      checkin_date: conversation?.reservation?.checkin_date,
      checkout_date: conversation?.reservation?.checkout_date
    };

    const formattedProperty = property
      ? {
          id: property?.id,
          home_code: property?.home_code,
          locality: {
            name: property?.locality?.name,
            utc_offset: property?.locality?.utc_offset
          },
          pod: {
            name: property?.pod?.name
          },
          accommodates: property?.accommodates,
          sorted_from: property?.sorted_from,
          offboarded_from: property?.offboarded_from,
          host_preferences: {
            accept_long_bookings:
              property?.host_preferences?.accept_long_bookings,
            special_guidelines: property?.host_preferences?.special_guidelines,
            allow_children: property?.host_preferences?.allow_children,
            allow_pets: property?.host_preferences?.allow_pets,
            special_price: property?.host_preferences?.special_price
          },
          full_address: property?.full_address,
          postcode: property?.postcode,
          guest_key: property?.guest_key,
          home_info: property?.home_info
            ? {
                property_lat: property?.home_info?.location_property_lat,
                property_lng: property?.home_info?.location_property_lng,
                unit_floor: property?.home_info?.unit_floor,
                digital_lock_code:
                  property?.home_info?.location_digital_lock_code,
                lockbox_code: property?.home_info?.location_lockbox_code,
                access_type: property?.home_info?.location_access_type,
                keynest_key: property?.home_info?.keynest_key, // NOT SURE IF WE HAVE THIS IN THE ENDPOINT
                wifi_network_name: property?.home_info?.wifi_network_name,
                wifi_password: property?.home_info?.wifi_password,
                wifi_router_location: property?.home_info?.wifi_router_location, // NOT SURE IF WE HAVE THIS IN THE ENDPOINT
                earliest_checkin_time:
                  property?.home_info?.earliest_checkin_time,
                latest_checkout_time: property?.home_info?.latest_checkout_time,
                parking_notes: property?.home_info?.parking_notes,
                heating_notes: property?.home_info?.maintenance_heating_notes,
                air_conditioning_notes:
                  property?.home_info?.maintenance_air_conditioning_notes,
                hot_water_notes:
                  property?.home_info?.maintenance_hot_water_notes,
                washing_machine_notes:
                  property?.home_info?.washing_machine_notes, // NOT SURE IF WE HAVE THIS IN THE ENDPOINT
                tv_instructions:
                  property?.home_info?.maintenance_tv_instructions,
                nearest_station: property?.home_info?.location_nearest_station,
                nearest_supermarket:
                  property?.home_info?.location_nearest_supermarket,
                unit_noise_level: property?.home_info?.unit_noise_level,
                unit_noise_level_notes:
                  property?.home_info?.unit_noise_level_notes
              }
            : null,
          account_manager: property?.account_manager,
          lead: {
            first_name: property?.lead?.first_name,
            last_name: property?.lead?.last_name,
            contact_timezone: property?.lead?.contact_timezone,
            phones: property?.lead?.phones
          },
          dashboard_link: property?.dashboard_link,
          comments: property?.comments
        }
      : null;

    const formattedBookings = bookings?.map(booking => ({
      id: booking?.id,
      property: formattedProperty,
      channel: booking?.channel,
      booking_channel: booking?.booking_channel,
      status: booking?.status,
      // Combined datetime
      checkin_date: booking?.checkin_datetime,
      checkout_date: booking?.checkout_datetime,
      checkin_datetime: booking?.checkin_datetime,
      checkout_datetime: booking?.checkout_datetime,
      // Only dates
      checkin: booking?.checkin,
      checkout: booking?.checkout,
      // Only times
      checkin_time: booking?.checkin_time,
      checkout_time: booking?.checkout_time,
      num_guests: booking?.num_guests,
      approved: booking?.approved,
      reviewer: {
        full_name: booking?.reviewer?.full_name
      },
      cancellation: booking?.cancellation,
      early_checkin: {
        applicable: booking?.early_checkin?.applicable,
        status: booking?.early_checkin?.status,
        explanation: booking?.early_checkin?.explanation
      },
      guest_photo_url: booking?.guest_photo_url,
      guest_name: booking?.guest_name,
      unhappy_guest: booking?.unhappy_guest,
      unhappy_guest_marked_at: booking?.unhappy_guest_marked_at,
      unhappy_guest_notes: booking?.unhappy_guest_notes,
      comments: booking?.comments
    }));

    return {
      bookings: formattedBookings,
      property: formattedProperty,
      conversation,
      reservation
    };
  }, [bookings, conversation, property]);

  return {
    data: { bookings, conversation, property },
    select,
    isLoading: isBookingsLoading || isConversationLoading || isPropertyLoading,
    error: bookingsError || conversationError || propertyError,
    refetchConversation,
    refetchBookings
  };
};
